import { useState, useMemo, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { Bar, Line } from "recharts";
import ChartConfigModal from "@plannel/components/modal/ChartConfigModal";
import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import { KpiSalesCard } from "@plannel/components/dashboard/KpiCard";
import { SegToggle, BucketToggle } from "@plannel/components/dashboard/DashboardToggle";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import SeriesLegend from "@plannel/components/dashboard/SeriesLegend";
import { DashboardComposedChart, barOrLine, areaGradientDefs, DW_CHART_TYPES } from "@plannel/components/dashboard/DashboardChart";
import WorldMapPanel from "@plannel/components/dashboard/WorldMapPanel";
import {
  dashboardDefaultColDef, dashboardGridSx, DASHBOARD_ROW_HEIGHT, HIGHLIGHT_ROW_CLASS, RatioBarCell,
} from "@plannel/components/dashboard/dashboardGrid";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import useDashboardP13n, { MAX_TOP_N } from "@plannel/hooks/useDashboardP13n";
import ColorUtil from "@plannel/pages/utils/color-util";
import codeUtil from "@plannel/pages/utils/code-util";
import salesStatusService from "@plannel/services/dashboard/sales-status-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ZDate from "@plannel/utils/zdate";
import { FS } from "@plannel/utils/font-util";

// 트렌드 시리즈 색상은 SalesTrendChart 내부에서 렌더 시점에 계산한다(아래).
//   모듈 상수로 굳히면 앱 부팅 시 사용자 팔레트(App.js 가 로그인 후 로드)보다 먼저 default 로 고정돼 반영 안 됨.

// 카드별 차트 설정 기본값(z_p13n config_obj.chart, 사용자별). 시리즈별 {chartType, activeFlg}.
// growth 는 % 축이라 기본 LINE. itemGroup 은 전체 공통(activeFlg 없음).
const DEFAULT_CHART_CONFIG = {
  actual: { chartType: "AREA", activeFlg: true },   // 대표 시리즈는 그라데이션 영역(Inventory2 감성)
  prevYear: { chartType: "LINE", activeFlg: true },
  mf: { chartType: "LINE", activeFlg: true },
  ff: { chartType: "LINE", activeFlg: true },   // 목표는 라인이 읽기 쉬움
  growth: { chartType: "LINE", activeFlg: true },
  itemGroup: { chartType: "BAR" },
};


// ─────────────────────────────────────────
// 상단 KPI: 최다 판매 품목
// ─────────────────────────────────────────
const TopItemsCard = ({ topItems, fmtMoney, unit, onClick }) => {
  const { t } = useTranslation();
  return (
    <Card
      onClick={onClick}
      sx={{
        flex: 1,
        // 클릭 어포던스는 KpiCard(onClick) 와 동일 — KPI 3장이 같은 hover 룩을 갖도록
        ...(onClick && {
          cursor: "pointer",
          transition: CARD_HOVER_TRANSITION,
          "&:hover": CARD_HOVER_SX,
        }),
      }}>
      <DashboardCardHeader title={t("ssKpiTopItems")} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, mt: 0.3 }}>
        {topItems.map((it) => (
          <Box key={it.rank} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: FS.SUB, color: "text.primary", fontWeight: 500 }}>
              {it.rank}{t("ssRankSuffix")} &nbsp; {it.code}
            </Typography>
            <Typography sx={{ fontSize: FS.SUB, color: "text.primary", fontWeight: 600 }}>
              {unit === "AMT" ? fmtMoney(it.amt) : (it.qty ?? 0).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

// ─────────────────────────────────────────
// 차트 1: 판매 현황 (실적 / 전년동기 / MF / 연간목표 / 성장률)
// ─────────────────────────────────────────
// 트렌드 시리즈별 팔레트 인덱스 — 차트 색(SERIES)과 설정 모달 스와치(configMeasures)가 공유한다.
const TREND_COLOR_IDX = { actual: 0, prevYear: 14, mf: 2, ff: 3, growth: 1 };

const SalesTrendChart = ({ data, currencyCd, unit, bucket, weekAvailable, onChangeBucket, chartConfig, onOpenConfig, onToggleSeries, onDrill }) => {
  const { t } = useTranslation();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const fmtBy = unit === "AMT" ? fmtMoney : fmtQty;
  const growthLabel = t("growthLabel");
  const isWeek = bucket === "week";   // 주 뷰: 전년동기·성장률 제외

  // 트렌드 시리즈 색상 — 마운트 시점에 활성 팔레트로 계산(사용자 프리셋 반영).
  //   색 인덱스는 TREND_COLOR_IDX(모듈 상수)로 설정 모달 스와치와 공유 → 색 불일치 방지.
  const SERIES = useMemo(() => ({
    actual: ColorUtil.colorSet(TREND_COLOR_IDX.actual),
    prevYear: ColorUtil.colorSet(TREND_COLOR_IDX.prevYear),
    mf: ColorUtil.colorSet(TREND_COLOR_IDX.mf),
    ff: ColorUtil.colorSet(TREND_COLOR_IDX.ff),
    growth: ColorUtil.colorSet(TREND_COLOR_IDX.growth),
  }), []);

  const legend = useMemo(() => {
    const actual = { key: "actual", label: t("salesActual"), color: SERIES.actual };
    const mf = { key: "mf", label: t("managerForecast"), color: SERIES.mf };
    const ff = { key: "ff", label: t("annualTarget"), color: SERIES.ff };
    if (isWeek) return [actual, mf, ff];
    return [
      actual,
      { key: "prevYear", label: t("yoy"), color: SERIES.prevYear },
      mf,
      ff,
      { key: "growth", label: `${growthLabel} (%)`, color: SERIES.growth },
    ];
  }, [t, growthLabel, isWeek, SERIES]);

  // 시리즈 표시 = 설정 activeFlg. 범례 체크박스와 설정 모달이 동일 소스(chartConfig)를 공유해 동기화.
  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;

  // 제목(=드릴 링크) hover 를 카드 하이라이트로 펼친다 — Total Sales 등 KPI 카드와 같은 파란 보더.
  //   CSS 만으론 자식 hover 로 부모를 못 칠해서(:has 미의존) 상태로 올린다.
  const [drillHover, setDrillHover] = useState(false);

  return (
    <Card sx={{
      flex: 1, minHeight: 0,
      transition: CARD_HOVER_TRANSITION,
      ...(drillHover && CARD_HOVER_SX),
    }}>
      {/* 드릴 클릭 영역은 헤더 제목(차트 바깥) — 카드 전체로 잡으면 범례·툴팁·토글 클릭마다 탭이 튄다 */}
      <DashboardCardHeader
        title={t("ssChartSalesStatus")}
        onDrill={onDrill} drillTitle={t("menuDpPlanVsActual")} onDrillHover={setDrillHover}
        onOpenConfig={onOpenConfig}
      >
        <BucketToggle bucket={bucket} weekAvailable={weekAvailable} onChange={onChangeBucket} />
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data}
        yAxes={[
          { id: "qty", tickFormatter: fmtBy },
          ...(isWeek ? [] : [{ id: "pct", orientation: "right", tickFormatter: (v) => `${v}%` }]),
        ]}
        tooltipFormatter={(v, name) => (name === growthLabel ? `${v}%` : fmtBy(v))}
      >
        {areaGradientDefs([
          { dataKey: "actual", color: SERIES.actual },
          { dataKey: "prevYear", color: SERIES.prevYear },
          { dataKey: "mf", color: SERIES.mf },
          { dataKey: "ff", color: SERIES.ff },
          { dataKey: "growth", color: SERIES.growth },
        ])}
        {isActive("actual") && barOrLine(chartConfig, { key: "actual", dataKey: "actual", name: t("salesActual"), color: SERIES.actual, yAxisId: "qty", dflt: "AREA" })}
        {!isWeek && isActive("prevYear") && barOrLine(chartConfig, { key: "prevYear", dataKey: "prevYear", name: t("yoy"), color: SERIES.prevYear, yAxisId: "qty", dflt: "BAR" })}
        {isActive("mf") && barOrLine(chartConfig, { key: "mf", dataKey: "mf", name: t("managerForecast"), color: SERIES.mf, yAxisId: "qty", dflt: "BAR" })}
        {isActive("ff") && barOrLine(chartConfig, { key: "ff", dataKey: "ff", name: t("annualTarget"), color: SERIES.ff, yAxisId: "qty", dflt: "LINE" })}
        {!isWeek && isActive("growth") && barOrLine(chartConfig, { key: "growth", dataKey: "growth", name: growthLabel, color: SERIES.growth, yAxisId: "pct", dflt: "LINE" })}
      </DashboardComposedChart>
      <SeriesLegend items={legend} isActive={isActive} onToggle={onToggleSeries} />
    </Card>
  );
};

// ─────────────────────────────────────────
// 차트 2: 제품군별 판매 (품목 계층 선택 레벨, 동적 그룹)
// ─────────────────────────────────────────
const ItemGroupChart = ({ data, groups, currencyCd, unit, bucket, weekAvailable, onChangeBucket, chartConfig, onOpenConfig, levels, level, onChangeLevel }) => {
  const { t } = useTranslation();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const fmtBy = unit === "AMT" ? fmtMoney : fmtQty;
  const colorOf = (i) => ColorUtil.colorSet(i);

  // 범례 on/off 는 이 카드 로컬(임시) 상태 — 판매현황 카드와 달리 z_p13n 에 저장하지 않는다.
  const [hidden, setHidden] = useState({});
  const toggle = (g) => setHidden((s) => ({ ...s, [g]: !s[g] }));
  // 누적 막대의 최상단 세그먼트에만 라운드 처리
  const lastVisible = groups.filter((g) => !hidden[g]).slice(-1)[0];
  const igType = String(chartConfig?.itemGroup?.chartType ?? "BAR").toUpperCase();   // 전체 그룹 공통(BAR 누적 / LINE)

  const levelOptions = useMemo(() => (levels ?? []).map((lv) => ({
    value: lv.value,
    label: lv.label || lv.cd || `${t("level")} ${lv.value}`,
  })), [levels, t]);

  const legendItems = useMemo(() => groups.map((g, i) => ({ key: g, label: g, color: colorOf(i) })), [groups]);

  return (
    <Card sx={{ flex: 1, minHeight: 0 }}>
      <DashboardCardHeader title={t("ssChartItemGroup")} onOpenConfig={onOpenConfig}>
        {levelOptions.length > 1 && (
          <CompactSelect value={level} options={levelOptions} onChange={onChangeLevel} />
        )}
        <BucketToggle bucket={bucket} weekAvailable={weekAvailable} onChange={onChangeBucket} />
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data}
        yAxes={[{ tickFormatter: fmtBy }]}
        tooltipFormatter={(v) => fmtBy(v)}
        barCategoryGap="25%"
      >
        {groups.map((g, i) => !hidden[g] && (
          igType === "LINE"
            ? <Line key={g} type="monotone" dataKey={g} name={g} stroke={colorOf(i)} strokeWidth={2} dot={false} />
            : <Bar key={g} dataKey={g} name={g} stackId="items" fill={colorOf(i)} maxBarSize={44} radius={g === lastVisible ? [3, 3, 0, 0] : 0} />
        ))}
      </DashboardComposedChart>
      <SeriesLegend items={legendItems} isActive={(g) => !hidden[g]} onToggle={toggle} gap={1.2} />
    </Card>
  );
};

// ─────────────────────────────────────────
// 하단 좌측: 통계
// ─────────────────────────────────────────
const BottomStatsPanel = ({ stats, fmtMoney, onDrillAmt, onDrillQty }) => {
  const { t } = useTranslation();
  // 클릭 가능한 통계 — 커서 + hover 어포던스(Top Level Items 로 드릴스루)
  const linkSx = (on) => (on ? {
    cursor: "pointer", borderRadius: 1, mx: -0.5, px: 0.5,
    transition: "background-color 0.15s",
    "&:hover": { bgcolor: "action.hover" },
    "&:hover .dash-stat-val": { color: "primary.main" },
  } : {});
  return (
    <Card sx={{ flex: "0 0 200px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box onClick={onDrillAmt} sx={linkSx(onDrillAmt)}>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("ssBottomTotalAmt")}</Typography>
          <Typography className="dash-stat-val" sx={{ fontSize: FS.KPI, fontWeight: 700, color: "text.primary", mt: 0.3 }}>{fmtMoney(stats.totalAmt)}</Typography>
        </Box>
        <Box onClick={onDrillQty} sx={{ borderTop: 1, borderColor: "divider", pt: 1.5, ...linkSx(onDrillQty) }}>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("ssBottomTotalQty")}</Typography>
          <Typography className="dash-stat-val" sx={{ fontSize: FS.KPI, fontWeight: 700, color: "text.primary", mt: 0.3 }}>{(stats.totalQty ?? 0).toLocaleString()}</Typography>
        </Box>
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("ssBottomTopCustomer")}</Typography>
          <Typography sx={{ fontSize: FS.DISPLAY, fontWeight: 700, color: "text.primary", mt: 0.3 }}>{stats.topCustomer}</Typography>
        </Box>
      </Box>
    </Card>
  );
};

// ─────────────────────────────────────────
// 하단 우측: 상위 거래처 테이블
// ─────────────────────────────────────────
const TopCustomersTable = ({ customers, currencyCd, highlighted, onSelect, topN, onChangeTopN, onDrill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [drillHover, setDrillHover] = useState(false);   // 제목 hover → 카드 하이라이트(§9-1 표준)
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const gridRef = useRef(null);
  // 외부 선택 변경 시 행 클래스 갱신
  useEffect(() => { gridRef.current?.api?.redrawRows(); }, [highlighted]);

  const columnDefs = useMemo(() => [
    { headerName: t("rank"), valueGetter: (p) => p.node.rowIndex + 1, width: 90, cellStyle: { textAlign: "center" } },
    { headerName: t("customer"), field: "name", flex: 1, minWidth: 100 },
    { headerName: t("salesAmt"), field: "amt", width: 96, type: "rightAligned", valueFormatter: (p) => fmtMoney(p.value) },
    { headerName: t("quantity"), field: "qty", width: 84, type: "rightAligned", valueFormatter: (p) => (p.value ?? 0).toLocaleString() },
    { headerName: `${t("ratio")} (%)`, field: "pct", width: 130, cellRenderer: RatioBarCell, cellStyle: { display: "flex", alignItems: "center" } },
  ], [t, fmtMoney]);

  const topNOptions = useMemo(
    () => Array.from({ length: MAX_TOP_N }, (_, i) => ({ value: i + 1, label: t("topN", { n: i + 1 }) })),
    [t],
  );

  return (
    <Card sx={{
      flex: 1.4, minHeight: 0,
      transition: CARD_HOVER_TRANSITION,
      ...(drillHover && CARD_HOVER_SX),
    }}>
      {/* 그리드 행 클릭·Top N 셀렉트가 있어 카드 전체 클릭은 못 쓴다 → 제목만 링크 */}
      <DashboardCardHeader
        title={t("ssTableTopCustomers")} dense
        onDrill={onDrill} drillTitle={t("menuDpItemCustomerSummary")} onDrillHover={setDrillHover}
      >
        <CompactSelect value={topN} options={topNOptions} onChange={onChangeTopN} />
      </DashboardCardHeader>
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme)}>
        <AgGridReact
          ref={gridRef}
          theme="legacy"
          rowData={customers}
          columnDefs={columnDefs}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          suppressCellFocus
          onRowClicked={(e) => onSelect?.(e.data?.name)}
          getRowClass={(p) => (p.data?.name === highlighted ? HIGHLIGHT_ROW_CLASS : "")}
        />
      </Box>
    </Card>
  );
};


// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const SalesStatusDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [unit, setUnit] = useState(reduxViewState?.unit ?? "AMT");   // 페이지 공통 Amt/Qty 단위(상단 단일 토글이 KPI·차트 모두 제어)
  const [trendBucket, setTrendBucket] = useState(reduxViewState?.trendBucket ?? "month");   // 판매현황 카드 버킷(독립)
  const [igBucket, setIgBucket] = useState(reduxViewState?.igBucket ?? "month");             // 제품군 카드 버킷(독립)
  const [configOpen, setConfigOpen] = useState(false);
  const [configScope, setConfigScope] = useState("salesStatus");
  const openConfig = (scope) => { setConfigScope(scope); setConfigOpen(true); };
  const [selectedCustomer, setSelectedCustomer] = useState(null);   // 지도↔테이블 하이라이트 연동
  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevel ?? "");   // 제품군 계층 레벨("" = 최하위 기본)

  // 버킷은 재조회 불필요(월·주 모두 반환) — itemLevel(집계 레벨)만 재조회 트리거
  const { payload } = useDashboardData(salesStatusService.get, { itemLevel: itemLevel || undefined });

  // 사용자별 설정(z_p13n, pageCd = viewName) — 차트 타입 + 상위 거래처 Top N
  const { chartConfig, topN, saveChartConfig, saveTopN, toggleSeries } =
    useDashboardP13n(viewName, { defaultChartConfig: DEFAULT_CHART_CONFIG });

  const handleSaveConfig = (cfg) => { saveChartConfig(cfg); setConfigOpen(false); };

  // 드릴스루 공통 — 대상 뷰 상태 초기화 후 파라미터 심고 updateHistoryState 로 탭 전환(AI 드릴과 동일).
  //   reduxKey  : TabMenuList 의 대상 reduxKey(= viewName)
  //   makeState : (startDate, endDate) => 대상 페이지가 읽는 state 객체
  const drillWithPeriod = (reduxKey, makeState) => {
    const period = payload?.period;   // { from: "yyyy-MM", to: "yyyy-MM" }
    if (!period?.from || !period?.to) return;
    const [fy, fm] = period.from.split("-").map(Number);
    const [ty, tm] = period.to.split("-").map(Number);
    const startDate = new ZDate(fy, fm - 1, 1, 0, 0, 0);       // 시작월 1일
    const endDate = new ZDate(ty, tm, 0, 23, 59, 59);          // 종료월 말일
    reduxDispatch(reduxUtil.removeViewState({ viewName: reduxKey }));   // 이전 필터 제거 → 주입값 확실 적용
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(reduxKey, makeState(startDate, endDate))));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  };

  // Total Sales 카드 / Sales Status 차트 → DP Plan vs. Actual
  //   기간 + 버킷(월/주)만 심는다. 나머지는 removeViewState 로 지워져 대상 페이지 기본값이 되고, 그게 곧 요구사항이다:
  //     · 버전  = DpVersionSelect 가 reduxVersion 없으면 RELEASED 최신본을 자동 선택 → 최신 버전
  //     · 계층  = filter/itemFilterList/customerFilterList 가 비면 거래처·품목 계층 모두 All
  //   (아래 빈 객체는 그 "All" 의도를 코드에 남기는 것 — 지우면 동작은 같지만 의도가 사라진다)
  const openPlanVsActual = (drillBucket = "month") =>
    drillWithPeriod("DP_ACTUAL_PLAN", (startDate, endDate) => ({
      startDate,
      endDate,
      // 버킷: 카드 토글(월/주) 그대로. 단 대상 페이지는 **선택 버전의 계획 버킷이 우선**이라,
      //   DP 가 월계획이면 주로 넘겨도 월로 되돌린다(partialWeek 테넌트도 그쪽으로 정렬됨). 의도된 동작.
      timeDimension: drillBucket === "week" ? codeUtil.buckets.WEEK : codeUtil.buckets.MONTH,
      filter: {},                 // 거래처·품목 계층 트리 = All
      customerFilterList: {},     // 거래처 상세 필터 = All
      itemFilterList: {},         // 품목 상세 필터 = All
    }));

  // Top Selling Items 카드 / 하단 통계(상위 거래처 합계) → Top Level Items. 표시 단위를 리포트 toggle 로 넘김.
  //   대상 페이지는 period:{start,end}(ZDate) 와 toggle(소문자 "amt"/"qty") 을 읽는다.
  const openTopLevelItems = (drillUnit) =>
    drillWithPeriod("TOP_LEVEL_ITEM", (startDate, endDate) => ({
      period: { start: startDate, end: endDate },
      toggle: drillUnit === "QTY" ? "qty" : "amt",
    }));

  // 상위 거래처 카드 → Item Customer Summary(거래처 관점).
  //   ★ 이 페이지는 period:{start,end} 가 아니라 **top-level start/end** 를 읽는다(TopLevelItem 과 다름).
  //   레벨은 안 넘긴다 — hrchyLevel 을 비워 두면 대상이 type 의 최하위(= 거래처)를 자동 선택한다.
  // 권한 밖 화면으로 가는 드릴은 링크를 만들지 않는다(핸들러가 없으면 제목·카드가 평문이 된다).
  //   ⚠ ITEM_CUSTOMER_SUMMARY 는 appRoles: ["ROLE_APP_BF"] — BF 없는 테넌트엔 메뉴 자체가 없다.
  const openItemCustomerSummary = () =>
    drillWithPeriod("ITEM_CUSTOMER_SUMMARY", (startDate, endDate) => ({
      start: startDate,
      end: endDate,
      type: "customer",
    }));

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { unit, trendBucket, igBucket, itemLevel });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...reduxState }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, trendBucket, igBucket, itemLevel]);

  const fmtMoney = useMemo(() => makeFmtMoney(payload?.currencyCd), [payload]);

  // 상위 거래처 Top N 슬라이스 + 비중(pct) 재계산 — 지도/테이블 공유
  const shownCustomers = useMemo(() => {
    const list = (payload?.topCustomers ?? []).slice(0, topN);
    const sum = list.reduce((s, c) => s + (c.amt ?? 0), 0);
    return list.map((c) => ({ ...c, pct: sum > 0 ? Math.round((c.amt / sum) * 1000) / 10 : 0 }));
  }, [payload, topN]);

  // 하단 통계 = Top N 합계(백엔드 bottomStats 대신 슬라이스 기준으로 재계산)
  const bottomStats = useMemo(() => ({
    totalAmt: shownCustomers.reduce((s, c) => s + (c.amt ?? 0), 0),
    totalQty: shownCustomers.reduce((s, c) => s + (c.qty ?? 0), 0),
    topCustomer: shownCustomers[0]?.name ?? "",
  }), [shownCustomers]);

  if (!payload) return null;

  const kpis = payload.kpis ?? {};
  const weekAvailable = (payload.availableBuckets ?? ["month"]).includes("week");
  // 카드별 버킷 데이터 선택(월·주 모두 반환됨 → 재조회 없이 전환)
  const trendData = payload.salesTrend?.[trendBucket] ?? payload.salesTrend?.month ?? {};
  const igData = payload.itemGroup?.[igBucket] ?? payload.itemGroup?.month ?? {};

  // 설정 모달용 measure — 카드 스코프 + 해당 카드 버킷 반영(Week 뷰에선 전년동기·성장률 제외)
  const cfgBucket = configScope === "itemGroup" ? igBucket : trendBucket;
  const configMeasures = ({
    salesStatus: [
      { key: "actual", label: t("salesActual"), hasActive: true, color: ColorUtil.colorSet(TREND_COLOR_IDX.actual) },
      { key: "prevYear", label: t("yoy"), hasActive: true, monthOnly: true, color: ColorUtil.colorSet(TREND_COLOR_IDX.prevYear) },
      { key: "mf", label: t("managerForecast"), hasActive: true, color: ColorUtil.colorSet(TREND_COLOR_IDX.mf) },
      { key: "ff", label: t("annualTarget"), hasActive: true, color: ColorUtil.colorSet(TREND_COLOR_IDX.ff) },
      { key: "growth", label: t("growthLabel"), hasActive: true, monthOnly: true, color: ColorUtil.colorSet(TREND_COLOR_IDX.growth) },
    ],
    itemGroup: [
      { key: "itemGroup", label: t("ssChartItemGroup"), hasActive: false },
    ],
  }[configScope] ?? []).filter((r) => !(r.monthOnly && cfgBucket === "week"));

  return (
    <DashboardPage>
      {/* 상단 바: KPI 공통 Amt/Qty 통합 토글 + 데이터 범위(최근 12개월) */}
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        <SegToggle value={unit} options={["AMT", "QTY"]} onChange={setUnit} />
      </DashboardTopBar>

      {/* Row 1: KPI 3개 */}
      <DashboardRow kpi>
        <KpiSalesCard
          label={t("annualTarget")}
          kpi={kpis.annualTarget} fmtMoney={fmtMoney}
          sparkData={payload.spark?.annualTarget?.[unit] ?? []} gradientId="sparkAnnualTarget"
          unit={unit}
        />
        <KpiSalesCard
          label={t("ssKpiTotalSales")} sub={t("yoy")}
          kpi={kpis.totalSales} fmtMoney={fmtMoney}
          sparkData={payload.spark?.totalSales?.[unit] ?? []} gradientId="sparkTotalSales"
          unit={unit}
          onClick={isPermitted("DP_ACTUAL_PLAN") ? () => openPlanVsActual(trendBucket) : undefined}
        />
        <TopItemsCard
          topItems={kpis.topItems ?? []} fmtMoney={fmtMoney} unit={unit}
          onClick={isPermitted("TOP_LEVEL_ITEM") ? () => openTopLevelItems(unit) : undefined}
        />
      </DashboardRow>

      {/* Row 2: 판매현황 + 제품군별 */}
      <DashboardRow>
        <SalesTrendChart
          data={trendData[unit] ?? []}
          currencyCd={payload.currencyCd}
          unit={unit}
          bucket={trendBucket}
          weekAvailable={weekAvailable}
          onChangeBucket={setTrendBucket}
          chartConfig={chartConfig}
          onOpenConfig={() => openConfig("salesStatus")}
          onToggleSeries={toggleSeries}
          onDrill={isPermitted("DP_ACTUAL_PLAN") ? () => openPlanVsActual(trendBucket) : undefined}
        />
        <ItemGroupChart
          data={igData[unit] ?? []}
          groups={igData.groups ?? []}
          currencyCd={payload.currencyCd}
          unit={unit}
          bucket={igBucket}
          weekAvailable={weekAvailable}
          onChangeBucket={setIgBucket}
          chartConfig={chartConfig}
          onOpenConfig={() => openConfig("itemGroup")}
          levels={payload.itemLevels ?? []}
          level={itemLevel || payload.itemLevel || ""}
          onChangeLevel={setItemLevel}
        />
      </DashboardRow>

      {/* Row 3: 통계 + 상위거래처 지도 + 테이블 */}
      <DashboardRow>
        <BottomStatsPanel stats={bottomStats} fmtMoney={fmtMoney}
          onDrillAmt={isPermitted("TOP_LEVEL_ITEM") ? () => openTopLevelItems("AMT") : undefined}
          onDrillQty={isPermitted("TOP_LEVEL_ITEM") ? () => openTopLevelItems("QTY") : undefined} />
        {/* 마커 클릭 = 테이블 행 클릭과 같은 선택 + 현재 단위로 Top Level Items 드릴 */}
        <WorldMapPanel items={shownCustomers} currencyCd={payload.currencyCd}
          highlighted={selectedCustomer} onReset={() => setSelectedCustomer(null)}
          network={payload.network}
          onItemClick={(c) => {
            // 마커 선택은 지도 자체의 기능이라 권한과 무관하다 — 드릴만 막는다.
            setSelectedCustomer(c.name);
            if (isPermitted("TOP_LEVEL_ITEM")) openTopLevelItems(unit);
          }} />
        <TopCustomersTable customers={shownCustomers} currencyCd={payload.currencyCd}
          highlighted={selectedCustomer} onSelect={setSelectedCustomer}
          topN={topN} onChangeTopN={saveTopN}
          onDrill={isPermitted("ITEM_CUSTOMER_SUMMARY") ? openItemCustomerSummary : undefined} />
      </DashboardRow>

      <ChartConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        config={chartConfig}
        onSave={handleSaveConfig}
        measures={configMeasures}
        chartTypes={DW_CHART_TYPES}
      />
    </DashboardPage>
  );
};

export default SalesStatusDashboard;
